/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../../controls/Button';
import UsersTable from '../../UsersTable';
import AuthHeader from '../header';
import { deleteUser, updateClientStatus } from '../../../services/dashboard-service';
import { useData } from '../../../context/data.context';
import AddUserModal from '../../../modals/AddUser';
import { ActionData, UserDetails } from '../../../constants/telosair-interfaces';
import { TOAST_MESSAGES } from '../../../constants/global-constants';
import { GA_CATEGORIES, sendCustomEventToGa } from '../../../_helpers/ga';
import copyInviteLink from '../../../_helpers/inviteLink';
import { isAdminRole } from '../../../services/session-storage-service';

/**
 * Invite user component for account activation.
 * @returns - React compoennt for group setup.
 */
export default function AccountActivationInviteUser(): ReactElement {
  const { clientData, clientUsers, fetchAllUserList } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionData, setActionData] = useState<ActionData<UserDetails>>(
    {} as ActionData<UserDetails>
  );
  const navigate = useNavigate();

  /**
   * Function to complete onboarding process.
   */
  const onCompleteOnboarding = async (): Promise<void> => {
    try {
      if (clientData) {
        await updateClientStatus({ ...clientData, status: 'active' });
        toast.success('Onboarding completed successfully');
        sendCustomEventToGa(GA_CATEGORIES.onboarding, 'onboarding-completed');
        navigate('/');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'onboarding-failed');
    }
  };

  /**
   * Handles delete user.
   * @param id - User id.
   */
  const handleDeleteUser = async (id: number): Promise<void> => {
    try {
      await deleteUser(id);
      toast.success(TOAST_MESSAGES.deleted);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'user-deletion-completed');
      await fetchAllUserList();
    } catch (e) {
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'user-deletion-failed');
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<UserDetails>): Promise<void> => {
    if (action.type === 'delete') {
      await handleDeleteUser(action.data.id);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'user-deletion-initiated');
    } else if (action.type === 'copy-invite') {
      await copyInviteLink(action.data);
    } else {
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-editing-initiated');
      setActionData(action);
      setIsModalOpen(true);
    }
  };

  const users: (UserDetails & { action: (action: ActionData<UserDetails>) => void })[] =
    clientUsers.map((item) => ({
      ...item,
      action: handleAction
    }));

  /**
   * Function to handle Modal.
   */
  const handleModalClose = async (): Promise<void> => {
    try {
      if (actionData.type === 'edit') {
        sendCustomEventToGa(GA_CATEGORIES.onboarding, 'user-editing-completed');
      }
      setIsModalOpen(false);
      await fetchAllUserList();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  return (
    <div className="h-screen flex flex-col flex-1">
      <div className="flex flex-col justify-between sticky top-0 z-20">
        <AuthHeader />
        <div className="w-3/3 border-t-4 border-t-primary-500" />
      </div>
      <div className="h-full flex flex-1 md:block w-full">
        <div className="md:grid flex flex-col flex-1 md:container grid-cols-12 py-10 px-3 md:px-16 md:mx-auto h-full gap-3">
          <div className="col-span-2 w-full md:w-auto flex justify-center items-center md:items-start md:justify-start">
            <div className="w-4/5">
              <Button
                onClick={() => navigate(-1)}
                isBlockButton
                theme="secondary-color"
                size="sm"
                name="Go Back"
              />
            </div>
          </div>
          <div className="md:col-span-8 col-span-12 bg-container-700 p-6 rounded h-full flex flex-col max-h-[80%] md:max-h-[90%] overflow-auto">
            <div className="text-primary-500 text-2xl font-bold">
              Step 3 of 3: <span className="text-white">Invite Users</span>
            </div>
            <div className="text-gray-400 mt-1 leading-6">
              Personalize your TelosAir experience by creating profile and roles. Provide <br />{' '}
              essential details and preferences to unlock tailored alerts and make the <br /> most
              of our app.
            </div>
            <div className="flex justify-end items-end mt-8 text-gray-400 text-sm gap-5 rounded">
              {/* <div className="flex flex-1">
                <InputBox noMarginTop textValue={email} setText={setEmail} label="Email of user" />
              </div>
              <div className="flex flex-1">
                <Dropdown
                  noMarginTop
                  label="Set Role for User"
                  name="role"
                  options={ROLES.map((value) => ({ value }))}
                  handleOptionsSelect={(e) => setRole(e.target.value)}
                  field="value"
                />
              </div> */}
              <div className="w-40">
                <Button
                  onClick={() => setIsModalOpen(true)}
                  isBlockButton
                  name="Add User"
                  size="sm"
                  theme="secondary-gray"
                />
              </div>
            </div>
            <div className="mt-5">
              <UsersTable users={users} isAdmin={isAdminRole()} />
            </div>
          </div>
          <div className="col-span-2 flex-col md:items-end flex w-full md:w-auto items-center">
            <div className="w-4/5">
              <Button
                onClick={() => onCompleteOnboarding()}
                isBlockButton
                theme="primary"
                size="sm"
                name="Complete Invites"
              />
            </div>
            <div className="w-4/5 mt-3">
              <Button
                onClick={() => onCompleteOnboarding()}
                isBlockButton
                theme="secondary-color"
                size="sm"
                name="Skip setup"
              />
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && <AddUserModal actionData={actionData} handleClose={handleModalClose} />}
    </div>
  );
}
